import { Text, IconButton } from '@/shared/ui/primitives';
import type { FC, ReactElement, Ref } from 'react';
import { useId } from 'react';

import { sanitizeNumericInput } from '@/shared/lib/sanitizeNumericInput';
import { MinusIcon } from '@/shared/ui/icons/MinusIcon';
import { PlusIcon } from '@/shared/ui/icons/PlusIcon';
import { TEST_IDS } from '@/testIds';
import styles from './CreditBalanceStepper.module.css';

export type ActiveCredit = 'remaining' | 'total';

interface CreditBalanceStepperProps {
  remaining: string;
  total: string;
  activeCredit: ActiveCredit;
  unlimited: boolean;
  remainingInvalid: boolean;
  totalInvalid: boolean;
  decrementDisabled: boolean;
  incrementDisabled: boolean;
  remainingRef: Ref<HTMLInputElement>;
  totalRef: Ref<HTMLInputElement>;
  onActiveCreditChange: (value: ActiveCredit) => void;
  onRemainingChange: (value: string) => void;
  onTotalChange: (value: string) => void;
  onRemainingBlur: () => void;
  onTotalBlur: () => void;
  onStep: (delta: number) => void;
}

export const CreditBalanceStepper: FC<CreditBalanceStepperProps> = ({
  remaining,
  total,
  activeCredit,
  unlimited,
  remainingInvalid,
  totalInvalid,
  decrementDisabled,
  incrementDisabled,
  remainingRef,
  totalRef,
  onActiveCreditChange,
  onRemainingChange,
  onTotalChange,
  onRemainingBlur,
  onTotalBlur,
  onStep,
}) => {
  const remainingInputId = useId();
  const totalInputId = useId();
  const activeLabel = activeCredit === 'remaining' ? 'remaining' : 'total';
  const renderStepButton = (delta: -1 | 1): ReactElement => {
    const isDecrement = delta === -1;

    return (
      <IconButton
        type="button"
        variant="ghost"
        size="sm"
        className={styles.stepButton}
        disabled={unlimited || (isDecrement ? decrementDisabled : incrementDisabled)}
        aria-label={`${isDecrement ? 'Decrease' : 'Increase'} ${activeLabel} credits`}
        data-testid={
          isDecrement ? TEST_IDS.passCreditsStepperDecrement : TEST_IDS.passCreditsStepperIncrement
        }
        onClick={() => onStep(delta)}
      >
        {isDecrement ? <MinusIcon /> : <PlusIcon />}
      </IconButton>
    );
  };

  return (
    <div className={styles.control}>
      {unlimited ? (
        <span className={styles.unlimitedValue} aria-hidden="true">
          ∞
        </span>
      ) : (
        <>
          <div
            className={styles.creditPart}
            data-active={activeCredit === 'remaining'}
            role="group"
            aria-label="Remaining credits controls"
          >
            <div className={styles.valueRow}>
              <label
                className={styles.rowHitArea}
                htmlFor={remainingInputId}
                aria-label="Edit remaining credits"
              />
              {activeCredit === 'remaining' && renderStepButton(-1)}
              <input
                id={remainingInputId}
                ref={remainingRef}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                size={Math.max(1, Math.min(remaining.length, 8))}
                className={styles.creditValue}
                value={remaining}
                aria-label="Remaining credits"
                aria-invalid={remainingInvalid ? 'true' : 'false'}
                data-testid={TEST_IDS.passRemainingCreditsInput}
                onFocus={() => onActiveCreditChange('remaining')}
                onChange={(event) => onRemainingChange(sanitizeNumericInput(event.target.value))}
                onBlur={onRemainingBlur}
              />
              {activeCredit === 'remaining' && renderStepButton(1)}
              <label
                className={styles.rowHitArea}
                htmlFor={remainingInputId}
                aria-label="Edit remaining credits"
              />
            </div>
            <label htmlFor={remainingInputId} className={styles.captionHitArea}>
              <Text variant="caption" weight="regular" className={styles.creditCaption}>
                Remaining
              </Text>
            </label>
          </div>

          <span className={styles.separator} aria-hidden="true">
            /
          </span>

          <div
            className={styles.creditPart}
            data-active={activeCredit === 'total'}
            role="group"
            aria-label="Total credits controls"
          >
            <div className={styles.valueRow}>
              <label
                className={styles.rowHitArea}
                htmlFor={totalInputId}
                aria-label="Edit total credits"
              />
              {activeCredit === 'total' && renderStepButton(-1)}
              <input
                id={totalInputId}
                ref={totalRef}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                size={Math.max(1, Math.min(total.length, 8))}
                className={styles.creditValue}
                value={total}
                aria-label="Total credits"
                aria-invalid={totalInvalid ? 'true' : 'false'}
                data-testid={TEST_IDS.passTotalCreditsInput}
                onFocus={() => onActiveCreditChange('total')}
                onChange={(event) => onTotalChange(sanitizeNumericInput(event.target.value))}
                onBlur={onTotalBlur}
              />
              {activeCredit === 'total' && renderStepButton(1)}
              <label
                className={styles.rowHitArea}
                htmlFor={totalInputId}
                aria-label="Edit total credits"
              />
            </div>
            <label htmlFor={totalInputId} className={styles.captionHitArea}>
              <Text variant="caption" weight="regular" className={styles.creditCaption}>
                Total
              </Text>
            </label>
          </div>
        </>
      )}
    </div>
  );
};
